/**
 * Compatibility bridge between AngularJS-era persisted HTML and the React schema.
 *
 * IMPORTANT:
 * - Do not remove legacy parsing rules without testing real production forms.
 * - New React definitions are stored with DF_REACT_SCHEMA_V1.
 * - HTML attributes are sanitized before becoming customization metadata.
 * - The backend payload remains compatible with the existing C# repositories.
 */
import type {
  DynamicFormDefinition,
  FieldCustomization,
  FormColumn,
  FormField,
  FormRow,
  FormTab,
  LegacyPostedForm,
  LegacyPostedTab,
  SelectOption,
  VisibilityCondition
} from '../models/form';
import { buildLegacyElements } from '../utils/form-data';
import {
  createPersistedFormSettings,
  defaultSubmissionListSettings,
  normalizePersistedFormSettings
} from '../utils/form-settings';
import { createFieldName, createId } from '../utils/id';
import { readRuntimeUiLocale, type UiLocale } from '../app/ui-locale';
import { normalizeRegexDefinition } from './regex-literal';


function normalizeLegacyRegexForRead(rawPattern: string | undefined) {
  try {
    return normalizeRegexDefinition(rawPattern);
  } catch {
    // Old definitions must remain openable even when their literal flags are invalid.
    // The raw value is preserved so Designer validation blocks the next Save with a
    // field-level message instead of crashing during legacy HTML decoding.
    return { pattern: (rawPattern ?? '').trim(), flags: '', wasLiteral: false };
  }
}

const SCHEMA_PREFIX = 'DF_REACT_SCHEMA_V1:';


const LEGACY_CONTROL_ATTRIBUTES = new Set([
  'autocomplete', 'inputmode', 'pattern', 'min', 'max', 'step', 'dir', 'lang',
  'spellcheck', 'title', 'role'
]);
const LEGACY_WRAPPER_ATTRIBUTES = new Set(['dir', 'lang', 'title', 'role']);
const LEGACY_DESIGNER_DATA_ATTRIBUTES = new Set([
  'data-control-type', 'data-control-tag', 'data-template', 'data-dragtype',
  'data-draggable', 'data-droppable', 'data-editable', 'data-table-droppable',
  'data-delete-id', 'data-title', 'data-minimum-rows', 'data-validation-remote-url'
]);
const LEGACY_SYSTEM_CLASSES = new Set([
  'form-group', 'df-form-group', 'form-control', 'form-select', 'form-control-feedback', 'form-control-sm', 'form-control-lg', 'input-sm', 'input-lg',
  'btn', 'btn-xs', 'btn-sm', 'btn-md', 'btn-lg', 'btn-default', 'btn-secondary', 'btn-primary',
  'btn-success', 'btn-info', 'btn-warning', 'btn-danger', 'btn-link',
  'panel', 'panel-default', 'panel-primary', 'panel-success', 'panel-info',
  'panel-warning', 'panel-danger', 'panel-heading', 'panel-body', 'card', 'card-header', 'card-body', 'df-panel',
  'img-responsive', 'img-fluid', 'text-right', 'text-left', 'text-start', 'text-end', 'text-center', 'text-justify', 'text-inline', 'lead', 'text-danger',
  'text-muted', 'dir-ltr', 'dir-rtl', 'hidden', 'temporaryhidden',
  'radio-list__container', 'radio__list', 'uploadfile__container',
  'data-table__container', 'inputs__container', 'inserter__container',
  'table__container', 'table-responsive', 'minimum__message', 'errors__list',
  'help-block', 'form-text', 'invalid-feedback', 'is-invalid', 'form-check', 'form-check-input', 'form-check-label',
  'input-group', 'input-group-addon', 'input-group-text', 'center-block', 'input__container', 'checkbox__label', 'df-check', 'df-radio', 'df-choice-list'
]);

function isLegacyDeleted(element: Element): boolean {
  const deleted = element.closest('.temporaryhidden, [data-delete-id="1"]');
  return Boolean(deleted);
}

function extractLegacyAttributes(element: Element | null, allowed: Set<string>): Record<string, string> | undefined {
  if (!element) return undefined;
  const output: Record<string, string> = {};
  for (const attribute of Array.from(element.attributes)) {
    const name = attribute.name.toLowerCase();
    if (LEGACY_DESIGNER_DATA_ATTRIBUTES.has(name) || name.startsWith('ng-') || name.startsWith('on')) continue;
    if (!(name.startsWith('data-') || name.startsWith('aria-') || allowed.has(name))) continue;
    output[name] = attribute.value;
  }
  return Object.keys(output).length ? output : undefined;
}

function extractLegacyClasses(element: Element | null): string | undefined {
  if (!element) return undefined;
  const classes = Array.from(element.classList).filter((className) =>
    className && !LEGACY_SYSTEM_CLASSES.has(className) && !className.startsWith('ng-') &&
    !className.startsWith('has-') && className !== 'fa' && !className.startsWith('fa-')
  );
  return classes.length ? [...new Set(classes)].join(' ') : undefined;
}

function mergeClassNames(...values: Array<string | undefined>): string | undefined {
  const classes = values.flatMap((value) => value?.split(/\s+/).filter(Boolean) ?? []);
  return classes.length ? [...new Set(classes)].join(' ') : undefined;
}

function buildLegacyCustomization(control: Element, container: Element, formGroup: Element): FieldCustomization | undefined {
  const wrapper = formGroup !== control ? formGroup : container !== control ? container : null;
  const intermediate = control.parentElement && control.parentElement !== wrapper && wrapper?.contains(control.parentElement)
    ? control.parentElement
    : null;
  const customization: FieldCustomization = {
    customId: attr(control, 'id'),
    controlClass: mergeClassNames(extractLegacyClasses(control), extractLegacyClasses(intermediate)),
    controlStyle: attr(control, 'style'),
    controlAttributes: extractLegacyAttributes(control, LEGACY_CONTROL_ATTRIBUTES),
    wrapperClass: extractLegacyClasses(wrapper),
    wrapperStyle: wrapper ? attr(wrapper, 'style') : undefined,
    wrapperAttributes: extractLegacyAttributes(wrapper, LEGACY_WRAPPER_ATTRIBUTES)
  };
  return Object.values(customization).some((value) => value != null && value !== '') ? customization : undefined;
}

function bool(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return fallback;
}

function attr(element: Element, name: string): string | undefined {
  const value = element.getAttribute(name);
  return value == null || value === '' ? undefined : value;
}

function numberAttr(element: Element, name: string): number | undefined {
  const value = attr(element, name);
  if (value == null) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function styleValue(element: Element | null, property: string): string | undefined {
  if (!(element instanceof HTMLElement)) return undefined;
  const value = element.style.getPropertyValue(property).trim();
  return value || undefined;
}

function pixelStyle(element: Element | null, property: string): number | undefined {
  const value = styleValue(element, property);
  if (!value) return undefined;
  const match = /^([0-9]+(?:\.[0-9]+)?)px$/i.exec(value);
  return match ? Number(match[1]) : undefined;
}

function cleanModelPath(value: string | undefined): string {
  if (!value) return '';
  return value.replace(/^\s+|\s+$/g, '').replace(/^VM\./, '');
}

function parseVisibility(expression?: string): VisibilityCondition | undefined {
  if (!expression || expression === 'true' || expression.includes('undefined:undefined')) return undefined;
  const normalized = expression.trim().replace(/^\(+|\)+$/g, '');
  const operators: Array<[RegExp, VisibilityCondition['operator']]> = [
    [/\s*!==?\s*/, '!='],
    [/\s*===?\s*/, '=='],
    [/\s*>=\s*/, '>='],
    [/\s*<=\s*/, '<='],
    [/\s*>\s*/, '>'],
    [/\s*<\s*/, '<']
  ];

  for (const [pattern, operator] of operators) {
    const parts = normalized.split(pattern);
    if (parts.length === 2) {
      return {
        enabled: true,
        source: cleanModelPath(parts[0]),
        operator,
        value: (parts[1] ?? '').trim().replace(/^['"]|['"]$/g, '')
      };
    }
  }
  return undefined;
}

function getErrorText(container: Element, key: string): string | undefined {
  const target = container.querySelector(`[ng-message="${key}"], .error--${key}`);
  return target?.textContent?.trim() || undefined;
}

function mapLegacyType(raw: string): FormField['type'] {
  const type = raw.toLowerCase().replace(/^r-/, '');
  if (type === 'p' || type === 'label') return 'text';
  if (type === 'display-datetime') return 'display-datetime';
  if (type === 'display-label') return 'display-label';
  if (type === 'fileupload') return 'fileupload';
  if (type === 'simplecaptcha') return 'simplecaptcha';
  if (type === 'captcha') return 'captcha';
  if (type === 'table') return 'table';
  if (type === 'panel') return 'panel';
  if (type === 'image') return 'image';
  if (type === 'spacer') return 'spacer';
  if (type === 'button') return 'button';
  if (type === 'textarea') return 'textarea';
  if (type === 'select') return 'select';
  if (type === 'checkbox') return 'checkbox';
  if (type === 'radio') return 'radio';
  return 'input';
}

function findPrimaryControl(container: Element, rawType: string): Element {
  const type = mapLegacyType(rawType);
  if (type === 'input' || type === 'simplecaptcha') return container.matches('input') ? container : container.querySelector('input') ?? container;
  if (type === 'textarea') return container.matches('textarea') ? container : container.querySelector('textarea') ?? container;
  if (type === 'select') return container.matches('select') ? container : container.querySelector('select') ?? container;
  if (type === 'checkbox') return container.matches('input[type=checkbox]') ? container : container.querySelector('input[type=checkbox]') ?? container;
  if (type === 'fileupload') return container.matches('input[type=file]') ? container : container.querySelector('input[type=file]') ?? container;
  if (type === 'image') return container.matches('img') ? container : container.querySelector('img') ?? container;
  return container;
}

function parseOptions(container: Element, type: FormField['type']): SelectOption[] | undefined {
  const options: SelectOption[] = [];
  if (type === 'select') {
    for (const option of container.querySelectorAll('option')) {
      const value = option.getAttribute('value') ?? option.textContent ?? '';
      if (value.includes('undefined:undefined')) continue;
      options.push({ id: createId('opt'), label: option.textContent?.trim() || value, value, disabled: option.hasAttribute('disabled') });
    }
  } else if (type === 'radio') {
    for (const input of container.querySelectorAll('input[type=radio]')) {
      const id = input.getAttribute('id') ?? '';
      const label = (id ? container.querySelector(`label[for="${CSS.escape(id)}"]`) : input.closest('label'))?.textContent?.trim();
      options.push({ id: createId('opt'), label: label || input.getAttribute('data-title') || input.getAttribute('value') || 'گزینه', value: input.getAttribute('value') || label || '' , disabled: input.hasAttribute('disabled') });
    }
  }
  return options.length ? options : undefined;
}

function parseField(container: Element, tabName: string): FormField | null {
  if (isLegacyDeleted(container)) return null;
  const rawType = container.getAttribute('data-control-tag') || container.getAttribute('data-template') || container.tagName.toLowerCase();
  let type = mapLegacyType(rawType);
  const control = findPrimaryControl(container, rawType);
  // Some AngularJS-era definitions tagged the upload control as a generic
  // input even though the actual DOM element is input[type=file]. Promote it
  // to the canonical fileupload type so React never treats a file element as
  // a controlled text input.
  if (type === 'input' && (attr(control, 'type') ?? '').toLowerCase() === 'file') {
    type = 'fileupload';
  }
  const name = attr(control, 'name') || attr(control, 'id') || createFieldName(type);
  const formGroup = control.closest('.form-group') || container;
  const label = formGroup.querySelector('label');
  const title = attr(control, 'data-title') || label?.textContent?.trim() || attr(container, 'data-title') || (type === 'text' ? container.textContent?.trim() : '') || name;
  const visibility = parseVisibility(attr(formGroup, 'ng-show') || attr(container, 'ng-show'));
  const validationContainer = formGroup;
  const className = `${attr(control, 'class') ?? ''} ${attr(container, 'class') ?? ''}`;
  const legacyRegex = normalizeLegacyRegexForRead(attr(control, 'ng-pattern'));

  const field: FormField = {
    id: createId('field'),
    type,
    name,
    title,
    hidden: formGroup.classList.contains('hidden'),
    disabled: attr(control, 'ng-disabled') === 'ViewMode' || control.hasAttribute('disabled'),
    tabIndex: numberAttr(control, 'tabindex'),
    align: className.includes('text-center') ? 'center' : className.includes('text-left') || className.includes('dir-ltr') ? 'left' : 'right',
    alignmentExplicit: className.includes('text-center') || className.includes('text-left') || className.includes('text-start') || className.includes('text-justify'),
    size: className.includes('input-lg') || className.includes('btn-lg') ? 'lg' : className.includes('input-sm') || className.includes('btn-sm') ? 'sm' : 'md',
    visibility,
    customization: buildLegacyCustomization(control, container, formGroup),
    validation: {
      required: control.hasAttribute('required') || Boolean(attr(control, 'ng-required')),
      requiredMessage: getErrorText(validationContainer, 'required'),
      minLength: numberAttr(control, 'ng-minlength'),
      minLengthMessage: getErrorText(validationContainer, 'minlength'),
      maxLength: numberAttr(control, 'ng-maxlength') ?? numberAttr(control, 'maxlength'),
      maxLengthMessage: getErrorText(validationContainer, 'maxlength'),
      minValue: numberAttr(control, 'min'),
      minValueMessage: getErrorText(validationContainer, 'min'),
      maxValue: numberAttr(control, 'max'),
      maxValueMessage: getErrorText(validationContainer, 'max'),
      step: numberAttr(control, 'step'),
      stepMessage: getErrorText(validationContainer, 'step'),
      regex: legacyRegex.pattern || undefined,
      regexFlags: legacyRegex.flags || undefined,
      regexMessage: getErrorText(validationContainer, 'pattern'),
      mask: attr(control, 'ui-mask'),
      maskMessage: getErrorText(validationContainer, 'mask'),
      remoteUrl: attr(control, 'validation-remote') || attr(control, 'data-validation-remote-url'),
      remoteMessage: getErrorText(validationContainer, 'remote'),
      unique: control.hasAttribute('validation-unique'),
      uniqueMessage: getErrorText(validationContainer, 'unique'),
      custom: attr(control, 'custom-validation')?.includes('NationalCode') ? 'iranianNationalCode' : attr(control, 'custom-validation')?.includes('Numeric') ? 'numeric' : '',
      customMessage: getErrorText(validationContainer, 'custom'),
      accept: attr(control, 'accept'),
      acceptMessage: getErrorText(validationContainer, 'pattern'),
      minFileSizeKb: numberAttr(control, 'min-length'),
      minFileSizeMessage: getErrorText(validationContainer, 'minlength'),
      maxFileSizeKb: numberAttr(control, 'max-length'),
      maxFileSizeMessage: getErrorText(validationContainer, 'maxlength'),
      minimumRows: numberAttr(container, 'data-minimum-rows'),
      minimumRowsMessage: container.querySelector('.minimum__message')?.textContent?.trim() || undefined
    }
  };

  if (type === 'input') {
    field.inputType = (attr(control, 'type') as FormField['inputType']) || 'text';
    field.placeholder = attr(control, 'placeholder');
    field.defaultValue = attr(control, 'value');
    field.addonLeft = formGroup.querySelector('.input-group-addon:first-child, .input-group-text:first-child')?.textContent?.trim();
    field.addonRight = formGroup.querySelector('.input-group-addon:last-child, .input-group-text:last-child')?.textContent?.trim();
  } else if (type === 'textarea') {
    field.placeholder = attr(control, 'placeholder');
    field.defaultValue = control.textContent ?? '';
  } else if (type === 'select' || type === 'radio') {
    const options = parseOptions(formGroup, type);
    if (type === 'select') {
      const placeholder = options?.[0]?.value === '' ? options[0] : undefined;
      field.placeholder = placeholder?.label;
      field.options = placeholder ? options?.slice(1) : options;
      field.defaultValue = (control.querySelector('option[selected]') as HTMLOptionElement | null)?.value ?? '';
    } else {
      field.options = options;
      field.defaultValue = (control.querySelector('input[type=radio][checked]') as HTMLInputElement | null)?.value ?? '';
    }
  } else if (type === 'checkbox') {
    field.defaultValue = control.hasAttribute('checked') || attr(control, 'value') === 'True';
  } else if (type === 'fileupload') {
    field.placeholder = title;
  } else if (type === 'image') {
    field.imageUrl = attr(control, 'src') || attr(control, 'ng-src');
    field.imageAlt = attr(control, 'alt');
    field.imageWidth = numberAttr(control, 'width');
    field.imageHeight = numberAttr(control, 'height');
    field.responsive = control.classList.contains('img-responsive') || control.classList.contains('img-fluid');
    const binding = attr(control, 'ng-src');
    if (binding) field.imageBindingSource = cleanModelPath(binding.replace(/^[^+]*\+\s*/, '').replace(/["']/g, ''));
  } else if (type === 'text') {
    field.html = container.innerHTML;
    field.text = container.textContent?.trim();
    field.textColor = styleValue(control, 'color') || styleValue(container, 'color');
    const textClasses = new Set([...control.classList, ...container.classList]);
    field.textDisplay = textClasses.has('text-inline') ? 'inline' : 'block';
    field.textSize = textClasses.has('lead') ? 'lead' : 'normal';
    field.align = textClasses.has('text-center') ? 'center'
      : textClasses.has('text-left') || textClasses.has('text-start') ? 'left'
        : textClasses.has('text-justify') ? 'justify'
          : 'right';
    field.alignmentExplicit = field.align !== 'right';
  } else if (type === 'display-datetime') {
    field.dateDisplay = className.includes('label__datetext') ? 'dateText' : className.includes('label__timetext') ? 'timeText' : className.includes('label__date') ? 'date' : className.includes('label__time') ? 'time' : 'datetime';
  } else if (type === 'display-label') {
    field.displayBinding = { source: cleanModelPath(attr(container, 'ng-bind')), filter: 'none' };
  } else if (type === 'panel') {
    field.panelStyle = className.includes('panel-success') ? 'success' : className.includes('panel-info') ? 'info' : className.includes('panel-warning') ? 'warning' : className.includes('panel-danger') ? 'danger' : className.includes('panel-default') ? 'default' : 'primary';
    const heading = container.querySelector('.panel-heading, .card-header');
    field.title = heading?.textContent?.trim() || title;
    const body = container.querySelector('.panel-body, .card-body');
    field.panelColor = styleValue(heading, 'background-color') || styleValue(heading, 'background');
    field.panelTextColor = styleValue(heading, 'color');
    field.panelBodyColor = styleValue(body, 'background-color') || styleValue(body, 'background');
    field.children = body ? parseTopLevelFieldsOutsideRows(body, tabName) : [];
    field.panelRows = body ? parseDirectLegacyRows(body, tabName) : [];
  } else if (type === 'table') {
    const inputContainer = container.querySelector('.inputs__container');
    field.tableColumns = inputContainer ? parseTopLevelFieldsOutsideRows(inputContainer, tabName) : [];
    field.tableRows = inputContainer ? parseDirectLegacyRows(inputContainer, tabName) : [];
  } else if (type === 'spacer') {
    field.spacerHeight = pixelStyle(control, 'height') ?? pixelStyle(container, 'height') ?? 32;
  } else if (type === 'button') {
    field.text = (control as HTMLElement).textContent?.trim() || attr(control, 'value') || 'دکمه';
    field.buttonStyle = className.includes('btn-success') ? 'success' : className.includes('btn-info') ? 'info' : className.includes('btn-warning') ? 'warning' : className.includes('btn-danger') ? 'danger' : className.includes('btn-default') || className.includes('btn-secondary') ? 'default' : className.includes('btn-link') ? 'link' : 'primary';
    field.buttonAction = 'none';
  } else if (type === 'captcha') {
    field.captchaPublicKey = attr(control, 'key')?.replaceAll("'", '');
  } else if (type === 'simplecaptcha') {
    field.captchaLength = Math.max(3, Math.min(8, numberAttr(control, 'ng-minlength') ?? 4));
    field.captchaContainsLetters = attr(control, 'containsletter') === 'true';
    field.simpleCaptchaMode = field.captchaContainsLetters ? 'mixed' : 'digits';
    field.simpleCaptchaDigitStyle = 'persian';
    field.simpleCaptchaVisualStyle = 'easy';
    field.validation = { ...(field.validation ?? {}), required: field.validation?.required ?? true };
  }

  if (Object.values(field.validation ?? {}).every((value) => value === undefined || value === false || value === '')) delete field.validation;
  return field;
}

function isNestedWithinKnownControl(element: Element, root: Element): boolean {
  const parent = element.parentElement?.closest('[data-control-tag], [data-template]');
  return Boolean(parent && parent !== element && root.contains(parent));
}

function parseTopLevelFields(root: Element, tabName: string): FormField[] {
  const candidates = Array.from(root.querySelectorAll('[data-control-tag], [data-template]'));
  const output: FormField[] = [];
  const seen = new Set<Element>();

  for (const candidate of candidates) {
    if (seen.has(candidate) || isLegacyDeleted(candidate) || isNestedWithinKnownControl(candidate, root)) continue;
    const raw = candidate.getAttribute('data-control-tag') || candidate.getAttribute('data-template') || '';
    if (!raw || raw === 'label') continue;
    const field = parseField(candidate, tabName);
    if (field) output.push(field);
    seen.add(candidate);
  }
  return output;
}

function parseTopLevelFieldsOutsideRows(root: Element, tabName: string): FormField[] {
  const candidates = Array.from(root.querySelectorAll('[data-control-tag], [data-template]'));
  const output: FormField[] = [];
  for (const candidate of candidates) {
    if (isLegacyDeleted(candidate) || candidate.closest('.row') || isNestedWithinKnownControl(candidate, root)) continue;
    const raw = candidate.getAttribute('data-control-tag') || candidate.getAttribute('data-template') || '';
    if (!raw || raw === 'label') continue;
    const field = parseField(candidate, tabName);
    if (field) output.push(field);
  }
  return output;
}

function parseDirectLegacyRows(root: Element, tabName: string): FormRow[] {
  return Array.from(root.querySelectorAll('.row'))
    .filter((row) => !isLegacyDeleted(row) && !row.parentElement?.closest('.row') && !isNestedWithinKnownControl(row, root))
    .map((row) => parseLegacyRowElement(row, tabName));
}

function parseColumnSpan(element: Element): number {
  const classes = [...element.classList];
  for (const className of classes) {
    const match = /^col-(?:xs|sm|md|lg)-(\d+)$/.exec(className);
    if (match) return Number(match[1]);
  }
  return 12;
}

function parseFieldsForRow(container: Element, ownerRow: Element, tabName: string): FormField[] {
  const candidates = Array.from(container.querySelectorAll('[data-control-tag], [data-template]'));
  const output: FormField[] = [];
  for (const candidate of candidates) {
    if (isLegacyDeleted(candidate) || candidate.closest('.row') !== ownerRow) continue;
    if (isNestedWithinKnownControl(candidate, container)) continue;
    const raw = candidate.getAttribute('data-control-tag') || candidate.getAttribute('data-template') || '';
    if (!raw || raw === 'label') continue;
    const field = parseField(candidate, tabName);
    if (field) output.push(field);
  }
  return output;
}

function parseLegacyRowElement(row: Element, tabName: string): FormRow {
  const directColumns = Array.from(row.children).filter((child) =>
    !isLegacyDeleted(child) && [...child.classList].some((className) => /^col-(?:xs|sm|md|lg)-\d+$/.test(className))
  );
  const sourceColumns = directColumns.length ? directColumns : [row];
  const columns: FormColumn[] = sourceColumns.map((column) => {
    const nestedRows = Array.from(column.querySelectorAll('.row')).filter((candidate) =>
      !isLegacyDeleted(candidate) && candidate.parentElement?.closest('.row') === row
    );
    return {
      id: createId('col'),
      span: parseColumnSpan(column),
      fields: parseFieldsForRow(column, row, tabName),
      rows: nestedRows.map((nested) => parseLegacyRowElement(nested, tabName))
    };
  });
  return { id: createId('row'), columns };
}

function parseLegacyRows(html: string, tabName: string): FormRow[] {
  const parser = new DOMParser();
  const document = parser.parseFromString(`<div id="df-root">${html}</div>`, 'text/html');
  const root = document.getElementById('df-root');
  if (!root) return [{ id: createId('row'), columns: [{ id: createId('col'), span: 12, fields: [], rows: [] }] }];

  const rowElements = Array.from(root.querySelectorAll('.row')).filter((row) => !isLegacyDeleted(row) && !row.parentElement?.closest('.row'));
  if (!rowElements.length) {
    return [{ id: createId('row'), columns: [{ id: createId('col'), span: 12, fields: parseTopLevelFields(root, tabName), rows: [] }] }];
  }

  return rowElements.map((row) => parseLegacyRowElement(row, tabName));
}

function normalizeField(field: FormField): FormField {
  const legacyFileInput = field.type === 'input' && String(field.inputType ?? '').toLowerCase() === 'file';
  const normalized: FormField = legacyFileInput
    ? { ...field, type: 'fileupload', inputType: undefined, defaultValue: undefined }
    : { ...field };

  if (Array.isArray(normalized.children)) {
    normalized.children = normalized.children.map(normalizeField);
  }
  if (Array.isArray(normalized.tableColumns)) {
    normalized.tableColumns = normalized.tableColumns.map(normalizeField);
  }
  if (Array.isArray(normalized.tableRows)) {
    normalized.tableRows = normalized.tableRows.map(normalizeRow);
  }
  if (Array.isArray(normalized.panelRows)) {
    normalized.panelRows = normalized.panelRows.map(normalizeRow);
  }
  if (typeof normalized.designerTitle !== 'string') normalized.designerTitle = '';
  if (normalized.type === 'text') {
    normalized.textDisplay = normalized.textDisplay === 'inline' ? 'inline' : 'block';
    normalized.textSize = normalized.textSize === 'lead' ? 'lead' : 'normal';
    if (!['right', 'center', 'left', 'justify'].includes(String(normalized.align ?? ''))) normalized.align = 'right';
  }
  if (typeof normalized.alignmentExplicit !== 'boolean') {
    // RC4R3 and older React schemas stored right alignment as the implicit default.
    normalized.alignmentExplicit = Boolean(normalized.align && normalized.align !== 'right');
  }
  if (normalized.type === 'table') {
    normalized.tableColumns = Array.isArray(normalized.tableColumns) ? normalized.tableColumns : [];
    normalized.tableRows = Array.isArray(normalized.tableRows) ? normalized.tableRows : [];
  }
  if (normalized.type === 'fileupload') {
    const configuredMaximum = Number(normalized.validation?.maximumFiles ?? 0);
    const legacyDefault = (normalized.fileLimitVersion ?? 0) < 2
      && (normalized.fileMultiple === true || configuredMaximum > 1)
      && (!Number.isFinite(configuredMaximum) || configuredMaximum === 5);
    if (legacyDefault) {
      normalized.validation = { ...(normalized.validation ?? {}), maximumFiles: 10 };
    }
    normalized.fileLimitVersion = 2;
  }
  if (normalized.type === 'spacer' && (!Number.isFinite(normalized.spacerHeight) || Number(normalized.spacerHeight) < 0)) {
    normalized.spacerHeight = 32;
  }
  return normalized;
}

function normalizeRow(row: FormRow): FormRow {
  return {
    id: row.id || createId('row'),
    columns: (row.columns?.length ? row.columns : [{ id: createId('col'), span: 12, fields: [], rows: [] }]).map((column) => ({
      id: column.id || createId('col'),
      span: Number(column.span || 12),
      fields: Array.isArray(column.fields) ? column.fields.map(normalizeField) : [],
      rows: Array.isArray(column.rows) ? column.rows.map(normalizeRow) : []
    }))
  };
}

function formatPersianDate(date: Date): string {
  const parts = new Intl.DateTimeFormat('fa-IR-u-ca-persian-nu-latn', {
    year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Asia/Tehran'
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  return `${value('year')}/${value('month')}/${value('day')}`;
}

function localized(locale: UiLocale, fa: string, en: string, fr: string): string {
  if (locale === 'fr') return fr;
  if (locale === 'en') return en;
  return fa;
}

function defaultTab(index = 0, locale: UiLocale = readRuntimeUiLocale()): FormTab {
  return {
    name: `Page${index + 1}`,
    title: `${localized(locale, 'مرحله', 'Step', 'Étape')} ${index + 1}`,
    isReturnEnable: index > 0,
    isPreviewEnable: false,
    color: '',
    textColor: '',
    rows: [{ id: createId('row'), columns: [{ id: createId('col'), span: 12, fields: [], rows: [] }] }]
  };
}

/** Creates the default empty form used by the New Form workflow. */
export function createNewForm(locale: UiLocale = readRuntimeUiLocale()): DynamicFormDefinition {
  return {
    Id: '0',
    Name: `DynamicForm${Math.floor(Math.random() * 90000 + 10000)}`,
    Title: localized(locale, 'فرم من', 'My form', 'Mon formulaire'),
    TitleAlignment: 'inherit',
    TitleColor: '',
    ShowHeader: true,
    HeaderBackgroundColor: '',
    ShowDateRange: true,
    Description: localized(locale, 'توضیحات بیشتر فرم را وارد نمایید', 'Enter more details about the form', 'Saisissez une description du formulaire'),
    ExpireMessage: localized(locale, 'مهلت تکمیل این فرم به پایان رسیده است.', 'The time allowed to complete this form has expired.', 'La période de saisie de ce formulaire est terminée.'),
    StartDate: formatPersianDate(new Date()),
    EndDate: formatPersianDate(new Date(new Date().setFullYear(new Date().getFullYear() + 10))),
    IsInfinite: false,
    HaveCapacity: false,
    InstantSuspension: false,
    Capacity: 1,
    CapacityMessage: localized(locale, 'ظرفیت تکمیل شده است', 'The form capacity has been reached.', 'La capacité maximale du formulaire est atteinte.'),
    Enable: 'True',
    ShowSubmitCode: true,
    ShowFinalStage: true,
    SmsRecipients: '',
    SubmissionListSettings: defaultSubmissionListSettings(),
    Status: [],
    Tabs: [defaultTab(0, locale)]
  };
}

/** Decodes either a React-schema tab or an AngularJS HTML tab. */
export function decodeTab(tab: LegacyPostedTab, index: number): FormTab {
  const body = tab.DesignerBody ?? '';
  if (body.startsWith(SCHEMA_PREFIX)) {
    try {
      const parsed = JSON.parse(body.slice(SCHEMA_PREFIX.length)) as FormTab & { formSettings?: unknown };
      const { formSettings: _formSettings, ...tabSchema } = parsed;
      return {
        ...tabSchema,
        name: tab.Name || tabSchema.name,
        title: tab.Title || tabSchema.title,
        isReturnEnable: bool(tab.IsReturnEnable, tabSchema.isReturnEnable),
        isPreviewEnable: bool(tab.IsPreviewEnable, tabSchema.isPreviewEnable),
        color: tabSchema.color ?? '',
        textColor: tabSchema.textColor ?? '',
        rows: (tabSchema.rows ?? []).map(normalizeRow)
      };
    } catch (error) {
      console.error('Unable to decode React form schema; falling back to legacy HTML parser.', error);
    }
  }

  return {
    name: tab.Name || `Page${index + 1}`,
    title: tab.Title || `${localized(readRuntimeUiLocale(), 'مرحله', 'Step', 'Étape')} ${index + 1}`,
    isReturnEnable: bool(tab.IsReturnEnable),
    isPreviewEnable: bool(tab.IsPreviewEnable),
    color: '',
    textColor: '',
    rows: parseLegacyRows(body, tab.Name || `Page${index + 1}`)
  };
}

function readPersistedFormSettings(tabs: LegacyPostedTab[] | undefined) {
  for (const tab of tabs ?? []) {
    const body = tab.DesignerBody ?? '';
    if (!body.startsWith(SCHEMA_PREFIX)) continue;
    try {
      const parsed = JSON.parse(body.slice(SCHEMA_PREFIX.length)) as { formSettings?: unknown };
      if (Object.prototype.hasOwnProperty.call(parsed, 'formSettings')) {
        return normalizePersistedFormSettings(parsed.formSettings);
      }
    } catch {
      // decodeTab reports malformed schema and falls back to the legacy parser.
    }
  }
  return normalizePersistedFormSettings(undefined);
}

/** Converts the backend DTO into a complete React definition with safe defaults. */
export function decodeLegacyForm(source: LegacyPostedForm, formId: number): DynamicFormDefinition {
  const form = createNewForm();
  const persistedSettings = readPersistedFormSettings(source.Tabs);
  return {
    ...form,
    Id: String(formId),
    Name: source.Name || form.Name,
    Title: source.Title || form.Title,
    TitleAlignment: persistedSettings.titleAlignment,
    TitleColor: persistedSettings.titleColor,
    ShowHeader: persistedSettings.showHeader,
    HeaderBackgroundColor: persistedSettings.headerBackgroundColor,
    ShowDateRange: persistedSettings.showDateRange,
    Description: source.Description ?? '',
    ExpireMessage: source.ExpireMessage ?? form.ExpireMessage,
    StartDate: source.StartDate || form.StartDate,
    EndDate: source.EndDate || form.EndDate,
    IsInfinite: bool(source.IsInfinite),
    HaveCapacity: bool(source.HaveCapacity),
    InstantSuspension: bool(source.InstantSuspension),
    Capacity: Number(source.Capacity || 1),
    CapacityMessage: source.CapacityMessage ?? form.CapacityMessage,
    Enable: bool(source.Enable, true) ? 'True' : 'False',
    ShowSubmitCode: source.ShowSubmitCode ?? true,
    ShowFinalStage: source.ShowFinalStage ?? true,
    SmsRecipients: source.SmsRecipients ?? '',
    SubmissionListSettings: persistedSettings.submissionListSettings,
    Status: source.Status ?? [],
    Tabs: source.Tabs?.length ? source.Tabs.map(decodeTab) : [defaultTab(0)]
  };
}

function canonicalizeFieldRegex(field: FormField): FormField {
  const validation = field.validation ? { ...field.validation } : undefined;
  if (validation?.regex) {
    const normalized = normalizeRegexDefinition(validation.regex, validation.regexFlags ?? '');
    validation.regex = normalized.pattern;
    validation.regexFlags = normalized.flags || undefined;
  } else if (validation?.regexFlags) {
    validation.regexFlags = normalizeRegexDefinition('', validation.regexFlags).flags || undefined;
  }
  const mapRows = (rows: FormRow[] | undefined): FormRow[] | undefined => rows?.map((row) => ({
    ...row,
    columns: row.columns.map((column) => ({
      ...column,
      fields: column.fields.map(canonicalizeFieldRegex),
      rows: mapRows(column.rows)
    }))
  }));
  return {
    ...field,
    validation,
    children: field.children?.map(canonicalizeFieldRegex),
    tableColumns: field.tableColumns?.map(canonicalizeFieldRegex),
    panelRows: mapRows(field.panelRows),
    tableRows: mapRows(field.tableRows)
  };
}

function canonicalizeTabRegexes(tab: FormTab): FormTab {
  const mapRows = (rows: FormRow[]): FormRow[] => rows.map((row) => ({
    ...row,
    columns: row.columns.map((column) => ({
      ...column,
      fields: column.fields.map(canonicalizeFieldRegex),
      rows: column.rows ? mapRows(column.rows) : undefined
    }))
  }));
  return { ...tab, rows: mapRows(tab.rows) };
}

/** Serializes one tab; form-level metadata is stored once on the first tab. */
export function encodeTab(tab: FormTab, form?: DynamicFormDefinition): string {
  const payload: FormTab & { formSettings?: ReturnType<typeof createPersistedFormSettings> } = canonicalizeTabRegexes(tab);
  if (form) payload.formSettings = createPersistedFormSettings(form);
  return `${SCHEMA_PREFIX}${JSON.stringify(payload)}`;
}

/** Maps the React form model to PostedFormViewModel while preserving the existing handler contract. */
export function encodeFormForLegacyBackend(form: DynamicFormDefinition): LegacyPostedForm {
  return {
    Id: form.Id,
    Name: form.Name,
    Title: form.Title,
    Description: form.Description,
    ExpireMessage: form.ExpireMessage,
    StartDate: form.StartDate,
    EndDate: form.EndDate,
    IsInfinite: form.IsInfinite,
    HaveCapacity: form.HaveCapacity,
    InstantSuspension: form.InstantSuspension,
    Capacity: form.Capacity,
    CapacityMessage: form.CapacityMessage,
    Enable: form.Enable,
    ShowSubmitCode: form.ShowSubmitCode,
    ShowFinalStage: form.ShowFinalStage,
    SmsRecipients: form.SmsRecipients,
    Status: form.Status,
    Tabs: form.Tabs.map((tab, index) => ({
      Name: tab.name,
      Title: tab.title,
      IsReturnEnable: tab.isReturnEnable,
      IsPreviewEnable: tab.isPreviewEnable,
      DesignerBody: encodeTab(tab, index === 0 ? form : undefined),
      Elements: buildLegacyElements(form, tab.name)
    }))
  };
}

/** Creates a 12-unit Bootstrap-compatible layout with evenly distributed columns. */
export function createLayout(columnCount: number): FormRow {
  const safeCount = Math.max(1, Math.min(4, columnCount));
  const span = Math.floor(12 / safeCount);
  return {
    id: createId('row'),
    columns: Array.from({ length: safeCount }, () => ({ id: createId('col'), span, fields: [], rows: [] }))
  };
}

/** Creates a field with safe defaults suitable for immediate designer insertion. */
export function createField(type: FormField['type'], inputType?: FormField['inputType'], locale: UiLocale = readRuntimeUiLocale()): FormField {
  const field: FormField = {
    id: createId('field'),
    type,
    name: createFieldName(type),
    title: defaultTitle(type, locale),
    designerTitle: '',
    align: locale === 'fa' ? 'right' : 'left',
    alignmentExplicit: false,
    size: 'md',
    validation: {},
    visibility: { enabled: false, source: '', operator: '==', value: '' }
  };

  const optionOne = localized(locale, 'گزینه ۱', 'Option 1', 'Option 1');
  const optionTwo = localized(locale, 'گزینه ۲', 'Option 2', 'Option 2');

  switch (type) {
    case 'input': field.inputType = inputType ?? 'text'; field.placeholder = ''; field.inputPreset = ''; field.calculationDecimals = 0; field.calculationFormat = 'number'; if (field.inputType === 'number') field.title = localized(locale, 'فیلد عددی', 'Number field', 'Champ numérique'); break;
    case 'textarea': field.placeholder = ''; break;
    case 'select': field.lookupEnabled = false; field.lookupMode = 'static'; field.lookupMinChars = 1; field.lookupValueField = 'value'; field.lookupLabelField = 'label'; field.options = [{ id: createId('opt'), label: optionOne, value: '1' }, { id: createId('opt'), label: optionTwo, value: '2' }]; break;
    case 'radio': field.options = [{ id: createId('opt'), label: optionOne, value: '1' }, { id: createId('opt'), label: optionTwo, value: '2' }]; break;
    case 'checkbox': field.defaultValue = false; break;
    case 'panel': field.panelStyle = 'primary'; field.children = []; field.panelRows = []; break;
    case 'table': field.tableColumns = [createField('input', undefined, locale)]; field.tableRows = []; field.tableMobileMode = 'cards'; field.tableShowRowNumbers = true; field.tableAllowDuplicate = true; field.tableAllowReorder = true; field.validation = { minimumRows: 0 }; break;
    case 'fileupload': field.fileMultiple = true; field.fileLimitVersion = 2; field.validation = { maximumFiles: 10 }; break;
    case 'image': field.imageUrl = '/DesktopModules/FaraDynamicForms/Contents/assets/img/dashboard/image-placeholder.svg'; field.responsive = true; break;
    case 'text': field.text = localized(locale, 'این متن را ویرایش کنید', 'Edit this text', 'Modifiez ce texte'); field.html = field.text; field.textDisplay = 'block'; field.textSize = 'normal'; break;
    case 'spacer': field.spacerHeight = 32; break;
    case 'display-datetime': field.dateDisplay = 'datetime'; break;
    case 'display-label': field.displayBinding = { source: '', filter: 'none' }; break;
    case 'button': field.text = localized(locale, 'کلید ساده', 'Button', 'Bouton'); field.buttonStyle = 'primary'; field.buttonAction = 'none'; break;
    case 'captcha': field.captchaPublicKey = ''; break;
    case 'simplecaptcha': field.captchaLength = 4; field.captchaContainsLetters = false; field.simpleCaptchaMode = 'digits'; field.simpleCaptchaDigitStyle = locale === 'fa' ? 'persian' : 'latin'; field.simpleCaptchaVisualStyle = 'easy'; field.validation = { required: true }; break;
    default: break;
  }
  return field;
}

function defaultTitle(type: FormField['type'], locale: UiLocale): string {
  const fa: Record<FormField['type'], string> = {
    input: 'جعبه متنی', textarea: 'ناحیه متنی', select: 'جعبه کشویی', checkbox: 'چک‌باکس', radio: 'رادیوباکس',
    fileupload: 'آپلود فایل', table: 'جدول اطلاعاتی', panel: 'پنل', image: 'تصویر', text: 'متن', spacer: 'فاصله',
    button: 'دکمه', captcha: 'Google reCAPTCHA', simplecaptcha: 'کپچای داخلی', 'display-label': 'برچسب متصل', 'display-datetime': 'تاریخ و زمان'
  };
  const en: Record<FormField['type'], string> = {
    input: 'Text box', textarea: 'Text area', select: 'Dropdown', checkbox: 'Checkbox', radio: 'Radio buttons',
    fileupload: 'File upload', table: 'Data table', panel: 'Panel', image: 'Image', text: 'Text', spacer: 'Spacer',
    button: 'Button', captcha: 'Google reCAPTCHA', simplecaptcha: 'Built-in CAPTCHA', 'display-label': 'Bound label', 'display-datetime': 'Date and time'
  };
  const fr: Record<FormField['type'], string> = {
    input: 'Champ texte', textarea: 'Zone de texte', select: 'Liste déroulante', checkbox: 'Case à cocher', radio: 'Boutons radio',
    fileupload: 'Téléversement de fichier', table: 'Tableau de données', panel: 'Panneau', image: 'Image', text: 'Texte', spacer: 'Espace',
    button: 'Bouton', captcha: 'Google reCAPTCHA', simplecaptcha: 'CAPTCHA intégré', 'display-label': 'Libellé lié', 'display-datetime': 'Date et heure'
  };
  return locale === 'fr' ? fr[type] : locale === 'en' ? en[type] : fa[type];
}
